import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../../context/MaestroContext';
import maestroService from '../../maestroService';
import { Risco, RiscoEscalationEvent, SeveridadeRisco, StatusRisco } from '../../models/risco';
import { dispararAcaoSimulada } from '../../utils/acaoSimulada';
import { formatarData } from '../../utils/formatters';
import { COR_SEVERIDADE_RISCO, COR_STATUS_RISCO, LABEL_SEVERIDADE_RISCO, LABEL_STATUS_RISCO } from '../../utils/labels';
import Card from '../Card';
import EstadoVazio from '../EstadoVazio';

const SEVERIDADES: SeveridadeRisco[] = ['BAIXA', 'MEDIA', 'ALTA', 'CRITICA'];
const STATUS: StatusRisco[] = ['ABERTO', 'EM_TRATAMENTO', 'ESCALONADO', 'MITIGADO', 'ENCERRADO'];

const CardRisco: React.FC<{ risco: Risco; onAtualizado: (r: Risco) => void }> = ({ risco, onAtualizado }) => {
  const { getUsuario, empresasEscopo } = useMaestroContext();
  const [escalonando, setEscalonando] = useState(false);
  const [nivel, setNivel] = useState('GERENCIA_CONTABIL');
  const [motivo, setMotivo] = useState('');
  const [enviandoEscalonamento, setEnviandoEscalonamento] = useState(false);

  const [historico, setHistorico] = useState<RiscoEscalationEvent[] | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const empresa = empresasEscopo.find(e => e.id === risco.empresaId);
  const responsavel = getUsuario(risco.responsavelId);

  const mudarStatus = (novoStatus: string) => {
    maestroService.atualizarRisco(risco.id, {
      titulo: risco.titulo,
      descricao: risco.descricao,
      severidade: risco.severidade,
      status: novoStatus,
      recomendacaoAcao: risco.recomendacaoAcao,
      responsavelId: risco.responsavelId,
      prazoAcao: risco.prazoAcao,
    })
      .then(onAtualizado)
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível atualizar o status.'));
  };

  const confirmarEscalonamento = () => {
    if (!motivo.trim()) return;
    setEnviandoEscalonamento(true);
    maestroService.escalonarRisco(risco.id, { nivelEscalonamento: nivel, motivo: motivo.trim() })
      .then(atualizado => { onAtualizado(atualizado); setEscalonando(false); setMotivo(''); setHistorico(null); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível escalonar o risco.'))
      .finally(() => setEnviandoEscalonamento(false));
  };

  const alternarHistorico = () => {
    if (historico !== null) { setHistorico(null); return; }
    setCarregandoHistorico(true);
    maestroService.listarEscalonamentosDoRisco(risco.id)
      .then(setHistorico)
      .catch(() => setHistorico([]))
      .finally(() => setCarregandoHistorico(false));
  };

  return (
    <div className='mc-pdca-card'>
      <div className='mc-pdca-card__topo'>
        <span className={`mc-badge mc-badge--${COR_SEVERIDADE_RISCO[risco.severidade]}`}>{LABEL_SEVERIDADE_RISCO[risco.severidade]}</span>
        <span className={`mc-badge mc-badge--${COR_STATUS_RISCO[risco.status]}`}>{LABEL_STATUS_RISCO[risco.status]}</span>
      </div>
      <strong>{risco.titulo}</strong>
      {risco.descricao && <p>{risco.descricao}</p>}
      {risco.recomendacaoAcao && <p><strong>Recomendação:</strong> {risco.recomendacaoAcao}</p>}
      <div className='mc-pdca-card__rodape'>
        <span>{empresa?.nome ?? `Empresa #${risco.empresaId}`}{responsavel ? ` · ${responsavel.nome}` : ''}</span>
        <span>{risco.prazoAcao ? `Prazo: ${formatarData(risco.prazoAcao)}` : ''}</span>
      </div>

      <div className='mc-form-inline' style={{ marginTop: 10, marginBottom: 0 }}>
        <label>Status
          <select value={risco.status} onChange={e => mudarStatus(e.target.value)}>
            {STATUS.map(s => <option key={s} value={s}>{LABEL_STATUS_RISCO[s]}</option>)}
          </select>
        </label>
        <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setEscalonando(v => !v)}>
          <i className='bi bi-arrow-up-circle' /> Escalonar
        </button>
        <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={alternarHistorico}>
          {carregandoHistorico ? '...' : historico !== null ? 'Ocultar histórico' : 'Ver histórico'}
        </button>
      </div>

      {escalonando && (
        <div className='mc-form-inline' style={{ marginTop: 10 }}>
          <label>Nível de escalonamento
            <input type='text' placeholder='Ex.: GERENCIA_CONTABIL, DIRETORIA' value={nivel} onChange={e => setNivel(e.target.value)} />
          </label>
          <label>Motivo
            <input type='text' placeholder='Por que está escalonando' value={motivo} onChange={e => setMotivo(e.target.value)} />
          </label>
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={enviandoEscalonamento || !motivo.trim()} onClick={confirmarEscalonamento}>
            {enviandoEscalonamento ? 'Enviando...' : 'Confirmar'}
          </button>
        </div>
      )}

      {historico !== null && (
        historico.length === 0
          ? <p className='mc-texto-neutro' style={{ marginTop: 8 }}>Nenhum escalonamento registrado ainda.</p>
          : (
            <ul className='mc-wizard-checklist' style={{ marginTop: 8 }}>
              {historico.map(h => (
                <li key={h.id} className='is-ok'>
                  <i className='bi bi-arrow-up-circle' />
                  <div>
                    <strong>{h.nivelEscalonamento}{h.usuarioId ? '' : ' (automático)'}</strong>
                    <small>{h.motivo} · {formatarData(h.criadoEm)}</small>
                  </div>
                </li>
              ))}
            </ul>
          )
      )}
    </div>
  );
};

const VisaoRiscos: React.FC = () => {
  const { cicloAtivo, empresasEscopo } = useMaestroContext();
  const [riscos, setRiscos] = useState<Risco[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [criando, setCriando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [severidade, setSeveridade] = useState<SeveridadeRisco>('MEDIA');
  const [empresaId, setEmpresaId] = useState(0);
  const [enviando, setEnviando] = useState(false);

  const carregar = () => {
    if (!cicloAtivo.id) { setRiscos([]); setCarregando(false); return; }
    setCarregando(true);
    maestroService.listarRiscosPorCiclo(cicloAtivo.id)
      .then(setRiscos)
      .catch(() => setRiscos([]))
      .finally(() => setCarregando(false));
  };

  useEffect(carregar, [cicloAtivo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const atualizarNaLista = (atualizado: Risco) => setRiscos(prev => prev.map(r => r.id === atualizado.id ? atualizado : r));

  const criarRisco = () => {
    if (!titulo.trim() || !empresaId) return;
    setEnviando(true);
    maestroService.criarRisco({ cicloId: cicloAtivo.id, empresaId, titulo: titulo.trim(), descricao: descricao.trim() || undefined, severidade })
      .then(criado => { setRiscos(prev => [criado, ...prev]); setCriando(false); setTitulo(''); setDescricao(''); setSeveridade('MEDIA'); setEmpresaId(0); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o risco.'))
      .finally(() => setEnviando(false));
  };

  if (carregando) return <p className='mc-texto-neutro'>Carregando riscos...</p>;

  return (
    <Card title='Riscos do ciclo' subtitle='Classificação, tratamento e escalonamento (manual ou automático) dos riscos identificados.'>
      {!criando ? (
        <button type='button' className='mc-btn mc-btn--secondary' onClick={() => { setEmpresaId(empresasEscopo[0]?.id ?? 0); setCriando(true); }}>
          <i className='bi bi-plus-lg' /> Novo risco
        </button>
      ) : (
        <div className='mc-form-inline'>
          <label>Empresa
            <select value={empresaId} onChange={e => setEmpresaId(Number(e.target.value))}>
              <option value={0}>Selecione...</option>
              {empresasEscopo.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </label>
          <label>Severidade
            <select value={severidade} onChange={e => setSeveridade(e.target.value as SeveridadeRisco)}>
              {SEVERIDADES.map(s => <option key={s} value={s}>{LABEL_SEVERIDADE_RISCO[s]}</option>)}
            </select>
          </label>
          <label>Título
            <input type='text' placeholder='Resumo do risco' value={titulo} onChange={e => setTitulo(e.target.value)} />
          </label>
          <label>Descrição
            <input type='text' placeholder='Detalhes (opcional)' value={descricao} onChange={e => setDescricao(e.target.value)} />
          </label>
          <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !titulo.trim() || !empresaId} onClick={criarRisco}>
            {enviando ? 'Criando...' : 'Criar risco'}
          </button>
          <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setCriando(false)}>Cancelar</button>
        </div>
      )}

      {riscos.length === 0
        ? <EstadoVazio icone='bi bi-shield-exclamation' titulo='Nenhum risco registrado neste ciclo' descricao='Cadastre um risco para acompanhar classificação, tratamento e escalonamento.' />
        : <div className='mc-pdca-board mc-pdca-board--lista'>{riscos.map(r => <CardRisco key={r.id} risco={r} onAtualizado={atualizarNaLista} />)}</div>}
    </Card>
  );
};

export default VisaoRiscos;
